/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  DropdownOptionsV2,
  FieldSpec,
  ListMasterDataConfigsResponse,
  MasterDataCategoryV2,
  MasterDataDataType,
  MasterDataFieldType,
} from 'projects/admin/src/app/models/master-data-config';
import { MasterDataConfigService } from 'projects/admin/src/app/services/master-data-config.service';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';

@Component({
  selector: 'app-manage-master-data-config',
  templateUrl: './manage-master-data-config.component.html',
  styleUrls: ['./manage-master-data-config.component.scss'],
})
export class ManageMasterDataConfigComponent extends AnyoComponent {
  MasterDataDataType = MasterDataDataType;
  MasterDataFieldType = MasterDataFieldType;
  isDrawerVisible = false;
  isSubmitting = false;
  form: FormGroup;
  tableLoading = false;
  rows: Array<{
    category: MasterDataCategoryV2;
    masterDataTypes: string;
    configFields: FieldSpec[];
  }> = [];

  categories: string[] = [];
  masterDataTypes: string[] = [];
  dataTypes = Object.values(MasterDataDataType);
  fieldTypesAll = Object.values(MasterDataFieldType);
  dropdownOptions = Object.values(DropdownOptionsV2);

  constructor(
    auth: AuthService,
    private fb: FormBuilder,
    private toast: ToastrService,
    private service: MasterDataConfigService,
    private router: Router,
  ) {
    super(auth);
    this.form = this.fb.group({
      category: [null, Validators.required],
      masterDataTypes: [null, Validators.required],
      fields: this.fb.array([]),
    });
    // initialize with one field
    this.addField();

    // Prefill drawer when category matches an existing config
    this.form
      .get('category')!
      .valueChanges.subscribe((cat: MasterDataCategoryV2 | null) => {
        if (!cat) return;
        const existing = this.rows.find((r) => r.category === cat);
        if (existing) {
          // hydrate without looping back into this subscription
          this.editRow(existing, /*fromCategoryChange*/ true);
        } else {
          // fresh form state
          this.form.get('masterDataTypes')!.reset(null);
          const arr = new FormArray<FormGroup>([]);
          this.form.setControl('fields', arr);
          this.addField();
        }
      });
  }

  getDropdownOptions() {
    return [...this.categories, ...this.dropdownOptions].filter(
      (o) => o !== this.form.get('category')!.value,
    );
  }
  openDrawer() {
    this.isDrawerVisible = true;
    // reset form safely: clear fields and category only
    this.form.get('category')!.reset(null);
    this.form.get('masterDataTypes')!.reset(null);
    const arr = new FormArray<FormGroup>([]);
    this.form.setControl('fields', arr);
    this.addField();
  }

  closeDrawer() {
    this.isDrawerVisible = false;
  }

  get fields(): FormArray {
    return this.form.get('fields') as FormArray;
  }

  allowedFieldTypes(fg: FormGroup): MasterDataFieldType[] {
    const dt: MasterDataDataType | null = fg.get('dataType')!.value;
    if (dt === MasterDataDataType.Boolean) {
      return [MasterDataFieldType.Switch];
    }
    if (dt === MasterDataDataType.StringArray) {
      return [MasterDataFieldType.Dropdown];
    }
    if (dt === MasterDataDataType.Number) {
      return [MasterDataFieldType.Textbox];
    }
    if (dt === MasterDataDataType.String) {
      return this.fieldTypesAll.filter((t) => t !== MasterDataFieldType.Switch);
    }
    return this.fieldTypesAll;
  }

  allowedFieldTypesByIndex(index: number): MasterDataFieldType[] {
    const fg = this.fields.at(index) as FormGroup;
    return this.allowedFieldTypes(fg);
  }

  // No disable/enable on Generate Unique Id; the UI hides other controls instead

  private onDataTypeChanged(
    fg: FormGroup,
    dt: MasterDataDataType | null,
    preserveExistingValues: boolean = false,
  ) {
    // Reset dependent fields; guard against disabled controls
    // const currentFieldType = fg.get('fieldType')!.value;
    // const currentDropdownOptions = fg.get('dropdownOptions')!.value;

    if (fg.get('fieldType')!.enabled) {
      if (!preserveExistingValues) {
        fg.get('fieldType')!.setValue(null);
      }
    } else {
      fg.get('fieldType')!.enable({ emitEvent: false });
      if (!preserveExistingValues) {
        fg.get('fieldType')!.setValue(null);
      }
    }
    if (fg.get('dropdownOptions')!.enabled) {
      if (!preserveExistingValues) {
        fg.get('dropdownOptions')!.setValue(null);
      }
    } else {
      fg.get('dropdownOptions')!.enable({ emitEvent: false });
      if (!preserveExistingValues) {
        fg.get('dropdownOptions')!.setValue(null);
      }
    }

    // StringArray: hide Unique in UI, restrict FieldType to Dropdown, require dropdownOptions
    if (dt === MasterDataDataType.StringArray) {
      fg.get('unique')!.setValue(false);
      fg.get('fieldType')!.setValue(MasterDataFieldType.Dropdown, {
        emitEvent: false,
      });
    } else {
      // nothing to disable/enable; UI manages visibility
    }

    // Boolean: force Switch; UI restricts options
    if (dt === MasterDataDataType.Boolean) {
      fg.get('fieldType')!.setValue(MasterDataFieldType.Switch, {
        emitEvent: false,
      });
    } else {
      // leave fieldType enabled
    }

    if (dt === MasterDataDataType.Number) {
      fg.get('fieldType')!.setValue(MasterDataFieldType.Textbox, {
        emitEvent: false,
      });
    }

    if (
      dt === MasterDataDataType.String &&
      fg.get('fieldType')!.value === MasterDataFieldType.Switch
    ) {
      fg.get('fieldType')!.setValue(null, { emitEvent: false });
    }
  }

  private createFieldGroup(copyFrom?: FormGroup): FormGroup {
    const fg = this.fb.group({
      key: new FormControl<string | null>(copyFrom?.get('key')?.value ?? null),
      dataType: new FormControl<MasterDataDataType | null>(
        copyFrom?.get('dataType')?.value ?? null,
      ),
      fieldType: new FormControl<MasterDataFieldType | null>(
        copyFrom?.get('fieldType')?.value ?? null,
      ),
      unique: new FormControl<boolean>(copyFrom?.get('unique')?.value ?? false),
      mandatory: new FormControl<boolean>(
        copyFrom?.get('mandatory')?.value ?? false,
      ),
      dropdownOptions: new FormControl<DropdownOptionsV2 | null>(
        copyFrom?.get('dropdownOptions')?.value ?? null,
      ),

      customDropdownOptions: this.fb.array<FormControl<string | null>>(
        copyFrom
          ? (
              (copyFrom.get('customDropdownOptions')?.value as string[]) ?? []
            ).map((v) => new FormControl<string | null>(v))
          : [],
      ),
    });

    fg.get('dataType')!.valueChanges.subscribe(
      (dt: MasterDataDataType | null | undefined) => {
        this.onDataTypeChanged(fg, dt ?? null, false);
      },
    );

    // Ensure selecting FilePicker or ColorPicker forces dataType to String
    fg.get('fieldType')!.valueChanges.subscribe(
      (ft: MasterDataFieldType | null | undefined) => {
        if (
          ft === MasterDataFieldType.FilePicker ||
          ft === MasterDataFieldType.ColorPicker
        ) {
          fg.get('dataType')!.setValue(MasterDataDataType.String, {
            emitEvent: false,
          });
        }
      },
    );

    this.onDataTypeChanged(fg, fg.get('dataType')!.value ?? null, false);

    return fg;
  }

  addField(copyIndex?: number) {
    const fg = this.createFieldGroup(
      typeof copyIndex === 'number'
        ? (this.fields.at(copyIndex) as FormGroup)
        : undefined,
    );
    this.fields.push(fg);
  }

  duplicateField(index: number) {
    this.addField(index);
  }

  removeField(index: number) {
    if (this.fields.length > 1) {
      this.fields.removeAt(index);
    }
  }

  // Table
  async ready() {
    if (this.user) {
      this.getMasterDataCategories();
      await this.fetchAll();
    }
  }

  async fetchAll() {
    this.tableLoading = true;
    this.service.listAllConfigs().subscribe({
      next: (res: ListMasterDataConfigsResponse) => {
        const rowData = (res?.data || []).map((r) => ({
          category: r.category,
          masterDataTypes: r.masterDataType || '',
          configFields: r.config || [],
        }));
        this.rows = rowData;
        this.tableLoading = false;
      },
      error: () => {
        this.tableLoading = false;
        this.toast.error('Failed to load configs');
      },
    });
  }

  editRow(
    row: {
      category: MasterDataCategoryV2;
      masterDataTypes: string;
      configFields: FieldSpec[];
    },
    fromCategoryChange: boolean = false,
  ) {
    this.openDrawer();
    // avoid emitting to prevent re-entry when called from category change
    this.form
      .get('category')!
      .setValue(row.category, { emitEvent: fromCategoryChange ? false : true });

    const arr = new FormArray<FormGroup>([]);
    this.form.get('masterDataTypes')!.setValue(row.masterDataTypes);
    this.form.setControl('fields', arr);

    for (const f of row.configFields) {
      if (f.key === 'data') continue;

      const fg = this.createFieldGroup(); // start with defaults

      // Set up custom dropdown options first if needed
      if (
        f.dropdownOptions === DropdownOptionsV2.OTHERS &&
        f.customDropdownOptions
      ) {
        const opts = this.fb.array(
          (f.customDropdownOptions || []).map((o) => this.fb.control(o)),
        );
        fg.setControl('customDropdownOptions', opts);
      }

      // Set all values at once, including dropdownOptions
      fg.patchValue({
        key: f.key,
        dataType: f.dataType,
        fieldType: f.fieldType,
        unique: !!f.unique,
        mandatory: !!f.mandatory,
        dropdownOptions: f.dropdownOptions ?? null,
      });

      // Set up value change subscriptions
      fg.get('dropdownOptions')!.valueChanges.subscribe(
        (val: DropdownOptionsV2 | null | undefined) => {
          if (val === DropdownOptionsV2.OTHERS) {
            if (!fg.get('customDropdownOptions')) {
              (fg as any).addControl(
                'customDropdownOptions',
                this.fb.array([]),
              );
            }
          } else {
            if (fg.get('customDropdownOptions')) {
              (fg as any).removeControl('customDropdownOptions');
            }
          }
        },
      );

      fg.get('dataType')!.valueChanges.subscribe(
        (dt: MasterDataDataType | null | undefined) => {
          this.onDataTypeChanged(fg, dt ?? null, false);
        },
      );

      fg.get('fieldType')!.valueChanges.subscribe(
        (ft: MasterDataFieldType | null | undefined) => {
          if (
            ft === MasterDataFieldType.FilePicker ||
            ft === MasterDataFieldType.ColorPicker
          ) {
            fg.get('dataType')!.setValue(MasterDataDataType.String, {
              emitEvent: false,
            });
          }
        },
      );

      // Call onDataTypeChanged after all values are set to avoid resetting dropdownOptions
      // Use setTimeout to ensure all form values are properly set first
      // Pass preserveExistingValues=true to maintain dropdown values during edit
      setTimeout(() => {
        this.onDataTypeChanged(fg, fg.get('dataType')!.value ?? null, true);
      }, 0);

      arr.push(fg);
    }
  }

  addMasterData(category: MasterDataCategoryV2) {
    this.router.navigate(['/master-data-config/manage-master-data', category]);
  }

  submit() {
    const v = this.form.getRawValue();
    const configFeilds: Array<{
      key: string;
      dataType?: MasterDataDataType;
      fieldType?: MasterDataFieldType;
      unique?: boolean;
      mandatory?: boolean;
      dropdownOptions?: DropdownOptionsV2;
      customDropdownOptions?: string[];
    }> = [];

    for (const f of v.fields) {
      const field: {
        key: string;
        dataType?: MasterDataDataType;
        fieldType?: MasterDataFieldType;
        unique?: boolean;
        mandatory?: boolean;
        dropdownOptions?: DropdownOptionsV2;
        customDropdownOptions?: string[];
      } = { key: f.key ?? '' };

      if (f.dataType) field.dataType = f.dataType;
      if (f.fieldType) field.fieldType = f.fieldType;
      if (typeof f.mandatory === 'boolean') field.mandatory = !!f.mandatory;
      if (
        f.dataType !== MasterDataDataType.StringArray &&
        typeof f.unique === 'boolean'
      ) {
        field.unique = !!f.unique;
      }
      if (
        (f.dataType === MasterDataDataType.StringArray ||
          f.dataType === MasterDataDataType.String) &&
        f.fieldType === MasterDataFieldType.Dropdown &&
        f.dropdownOptions
      ) {
        field.dropdownOptions = f.dropdownOptions;
      }
      if (
        f.dropdownOptions === DropdownOptionsV2.OTHERS &&
        f.customDropdownOptions &&
        f.customDropdownOptions.length > 0
      ) {
        field.customDropdownOptions = f.customDropdownOptions;
      }
      configFeilds.push(field);
    }

    const body = {
      category: v.category as MasterDataCategoryV2,
      masterDataType: v.masterDataTypes as string,
      configFeilds,
    };
    this.isSubmitting = true;
    this.service.createOrUpdateMasterDataConfig(body).subscribe({
      next: async () => {
        await this.fetchAll();
        this.toast.success('Master Data Config saved');
        this.isSubmitting = false;
        this.closeDrawer();
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.error('Failed to save config');
        // Optionally log err
      },
    });
  }

  getMasterDataCategories() {
    this.service.getMasterDataCategories().subscribe({
      next: (res: { categories: string[]; masterDataTypes: string[] }) => {
        this.categories = res.categories || [];
        this.masterDataTypes = res.masterDataTypes || [];
      },
      error: () => {
        this.toast.error('Failed to load categories');
      },
    });
  }

  // Access dropdownOptions FormArray for a specific field index
  dropdownOptionsControl(
    fieldIndex: number,
  ): FormControl<DropdownOptionsV2 | null> {
    const fieldsArray = this.form.get('fields') as FormArray;
    return fieldsArray
      .at(fieldIndex)
      .get('dropdownOptions') as FormControl<DropdownOptionsV2 | null>;
  }

  // Access customDropdownOptions FormArray for a specific field index
  customDropdownOptionsControl(fieldIndex: number): FormArray {
    const fieldsArray = this.form.get('fields') as FormArray;
    const fg = fieldsArray.at(fieldIndex) as FormGroup;

    // Ensure FormArray exists
    if (!fg.get('customDropdownOptions')) {
      fg.addControl('customDropdownOptions', this.fb.array([]));
    }

    return fg.get('customDropdownOptions') as FormArray;
  }

  // Add custom value to FormArray
  addCustomOption(fieldIndex: number, value: string) {
    if (!value) return;

    const customOptions = this.customDropdownOptionsControl(fieldIndex);
    // Avoid duplicates
    if (!customOptions.value.includes(value)) {
      customOptions.push(this.fb.control(value));
    }
  }

  // Remove value from FormArray
  removeCustomOption(fieldIndex: number, optionIndex: number) {
    const customOptions = this.customDropdownOptionsControl(fieldIndex);
    customOptions.removeAt(optionIndex);
  }
}
